import { Link, useLocation } from "react-router";
import { t, type MessageKey } from "@/lib/i18n/messages";
import { useAppContext } from "@/providers";
import { useDashboardShell } from "@/lib/layout/use-dashboard-shell";
import { Logo } from "@/components/layout/logo";
import { cn } from "@/lib/utils";

export function DashboardSidebar() {
  const { pathname } = useLocation();
  const { locale } = useAppContext();
  const {
    isSidebarCollapsed,
    toggleSidebar,
    isMobile,
    isDrawerOpen,
    closeDrawer,
  } = useDashboardShell();

  const links: { icon: string; labelKey: MessageKey; href: string }[] = [
    {
      icon: "account_balance_wallet",
      labelKey: "navWallet",
      href: "/dashboard",
    },
    {
      icon: "bar_chart",
      labelKey: "navAnalytics",
      href: "/dashboard/analytics",
    },
    { icon: "lightbulb", labelKey: "initiatives", href: "/dashboard/initiatives" },
    {
      icon: "volunteer_activism",
      labelKey: "navContributions",
      href: "/dashboard/contributions",
    },
    { icon: "settings", labelKey: "navSettings", href: "/dashboard/settings" },
  ];

  const sidebarContent = (
    <>
      <div className="p-4 flex items-center justify-between gap-3">
        {(!isSidebarCollapsed || isMobile) && <Logo size="md" />}
        {!isMobile && (
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={
              isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
            }
            className="p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted transition-colors duration-base flex-shrink-0"
          >
            <span className="material-icons-outlined text-xl">
              {isSidebarCollapsed ? "menu" : "menu_open"}
            </span>
          </button>
        )}
        {isMobile && (
          <button
            type="button"
            onClick={closeDrawer}
            aria-label="Close menu"
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors duration-base"
          >
            <span className="material-icons-outlined text-xl">close</span>
          </button>
        )}
      </div>
      <nav className="flex-1 px-3 space-y-1 mt-2">
        {links.map((link) => {
          const currentPath = pathname ?? "";
          const isActive =
            link.href === "/dashboard"
              ? currentPath === "/dashboard"
              : currentPath.startsWith(link.href);
          return (
            <Link
              key={link.href}
              to={link.href}
              onClick={isMobile ? closeDrawer : undefined}
              title={
                isSidebarCollapsed && !isMobile
                  ? t(locale, link.labelKey)
                  : undefined
              }
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-base",
                isActive
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-foreground-secondary hover:bg-muted hover:text-foreground"
              )}
              aria-current={isActive ? "page" : undefined}
            >
              <span className="material-icons-outlined flex-shrink-0">
                {link.icon}
              </span>
              {(!isSidebarCollapsed || isMobile) && (
                <span>{t(locale, link.labelKey)}</span>
              )}
            </Link>
          );
        })}
      </nav>
      {(!isSidebarCollapsed || isMobile) && (
        <div className="p-6 border-t border-border space-y-2 text-xs text-muted-foreground">
          <Link to="#" className="block hover:text-primary transition-colors">
            {t(locale, "sidebarFaq")}
          </Link>
          <Link to="#" className="block hover:text-primary transition-colors">
            {t(locale, "sidebarHowItWorks")}
          </Link>
          <Link to="#" className="block hover:text-primary transition-colors">
            {t(locale, "sidebarTerms")}
          </Link>
        </div>
      )}
    </>
  );

  // Mobile: drawer overlay
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        {isDrawerOpen && (
          <div
            className="fixed inset-0 z-40 bg-neutral-950/50 backdrop-blur-sm animate-fade-in"
            onClick={closeDrawer}
            aria-hidden="true"
          />
        )}
        {/* Drawer */}
        <aside
          className={cn(
            "fixed inset-y-0 start-0 z-50 w-72 bg-card border-e border-border flex flex-col",
            "transition-transform duration-slow ease-in-out",
            isDrawerOpen ? "translate-x-0" : "-translate-x-full rtl:translate-x-full"
          )}
          aria-label="Navigation"
        >
          {sidebarContent}
        </aside>
      </>
    );
  }

  // Desktop: fixed sidebar
  return (
    <aside
      className={cn(
        "fixed inset-y-0 start-0 z-50 bg-card border-e border-border flex flex-col overflow-hidden",
        "transition-all duration-slow ease-in-out",
        isSidebarCollapsed ? "w-16" : "w-64"
      )}
      aria-label="Navigation"
    >
      {sidebarContent}
    </aside>
  );
}
